import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authAPI, userAPI } from '../services/api';
import { trackLogin, trackSignUp, setUserId } from '../services/analytics';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [hasLoggedOut, setHasLoggedOut] = useState(false);
    const [accessToken, setAccessToken] = useState(() => localStorage.getItem('accessToken'));
    // eslint-disable-next-line no-unused-vars
    const [_refreshToken, setRefreshToken] = useState(() => localStorage.getItem('refreshToken'));
    const location = useLocation();

    const setBootstrapWatchedAddresses = (watchedAddresses = []) => {
        if (typeof window !== 'undefined') {
            window.__BN_BOOTSTRAP_WATCHED_ADDRESSES__ = Array.isArray(watchedAddresses)
                ? watchedAddresses
                : [];
        }
    };

    const clearBootstrapWatchedAddresses = () => {
        if (typeof window !== 'undefined') {
            window.__BN_BOOTSTRAP_WATCHED_ADDRESSES__ = null;
        }
    };

    const hydrateBootstrap = async () => {
        const bootstrap = await userAPI.getBootstrap();
        if (bootstrap?.user) {
            setUser(bootstrap.user);
        }
        setBootstrapWatchedAddresses(bootstrap?.watchedAddresses || []);
        return bootstrap;
    };
    
    // Public routes that don't need profile fetch
    const publicRoutes = [
        '/dashboard',
        '/news',
        '/products',
        '/blog',
        '/about',
        '/',
        '/docs',
        '/terms',
        '/privacy',
        '/support',
        '/contact',
        '/guide/slack',
        '/slack',
        '/help',
    ];
    
    const isPublicRoute = () => {
        return publicRoutes.some(route => location.pathname === route || location.pathname.startsWith(route + '/'));
    };

    useEffect(() => {
        checkAuthStatus();

        const handleAccessTokenUpdated = (event) => {
            const newToken = event?.detail?.accessToken;
            if (newToken) {
                setAccessToken(newToken);
            }
        };

        const handleRefreshTokenUpdated = (event) => {
            const newRefreshToken = event?.detail?.refreshToken;
            if (newRefreshToken) {
                setRefreshToken(newRefreshToken);
            }
        };

        // Soft logout triggered by api.js when token refresh fails —
        // avoids a hard window.location.href that destroys React state
        const handleForceLogout = () => {
            setUser(null);
            setIsAuthenticated(false);
            setAccessToken(null);
            setRefreshToken(null);
            setHasLoggedOut(true);
            clearBootstrapWatchedAddresses();
        };

        window.addEventListener('auth:tokenRefreshed', handleAccessTokenUpdated);
        window.addEventListener('auth:refreshTokenUpdated', handleRefreshTokenUpdated);
        window.addEventListener('auth:forceLogout', handleForceLogout);

        return () => {
            window.removeEventListener('auth:tokenRefreshed', handleAccessTokenUpdated);
            window.removeEventListener('auth:refreshTokenUpdated', handleRefreshTokenUpdated);
            window.removeEventListener('auth:forceLogout', handleForceLogout);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const checkAuthStatus = async () => {
        // Set a timeout to prevent infinite loading (especially in Safari)
        const timeoutId = setTimeout(() => {
            console.warn('⏰ Auth check timeout - forcing loading to false');
            setLoading(false);
        }, 8000); // 8 second timeout

        try {
            const token = localStorage.getItem('accessToken');
            const storedRefreshToken = localStorage.getItem('refreshToken');

            // If no tokens at all, immediately finish loading
            if (!token && !storedRefreshToken) {
                clearBootstrapWatchedAddresses();
                setLoading(false);
                clearTimeout(timeoutId);
                return;
            }

            // If no access token but refresh token exists, proactively refresh
            if (!token && storedRefreshToken) {
                try {
                    const refreshed = await authAPI.refreshToken(storedRefreshToken);
                    if (refreshed?.access?.token) {
                        localStorage.setItem('accessToken', refreshed.access.token);
                        setAccessToken(refreshed.access.token);
                    }
                    if (refreshed?.refresh?.token) {
                        localStorage.setItem('refreshToken', refreshed.refresh.token);
                        setRefreshToken(refreshed.refresh.token);
                    }
                } catch (e) {
                    // Refresh failed; clear tokens and let user re-authenticate
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('refreshToken');
                    setAccessToken(null);
                    setRefreshToken(null);
                    clearBootstrapWatchedAddresses();
                    setLoading(false);
                    clearTimeout(timeoutId);
                    return;
                }
            }

            const currentAccessToken = localStorage.getItem('accessToken');
            if (currentAccessToken) {
                setAccessToken(currentAccessToken);
                const currentRefreshToken = localStorage.getItem('refreshToken') || null;
                setRefreshToken(currentRefreshToken);

                const applyBootstrapPayload = (bootstrapPayload) => {
                    const profile = bootstrapPayload?.user || null;
                    const watchedAddresses = Array.isArray(bootstrapPayload?.watchedAddresses)
                        ? bootstrapPayload.watchedAddresses
                        : [];
                    if (profile) {
                        setUser(profile);
                    }
                    setBootstrapWatchedAddresses(watchedAddresses);
                };
                
                if (!isPublicRoute()) {
                    // Non-public routes: fetch profile before finishing (blocking)
                    try {
                        const bootstrap = await userAPI.getBootstrap();
                        applyBootstrapPayload(bootstrap);
                        setIsAuthenticated(true);
                    } catch (profileError) {
                        console.debug('Profile fetch failed:', profileError.message);
                        // A 401/403 here means the session is dead (refresh
                        // failed too, or the interceptor already cleared the
                        // tokens). Claiming isAuthenticated=true anyway left
                        // the dashboard stuck on skeletons with no way out —
                        // treat it as signed out so ProtectedRoute redirects
                        // to login. Network blips (no response) stay
                        // optimistic so a flaky connection doesn't log out.
                        const authStatus = profileError.response?.status;
                        if (authStatus === 401 || authStatus === 403) {
                            localStorage.removeItem('accessToken');
                            localStorage.removeItem('refreshToken');
                            setAccessToken(null);
                            setRefreshToken(null);
                            clearBootstrapWatchedAddresses();
                            setIsAuthenticated(false);
                        } else {
                            setIsAuthenticated(true);
                        }
                    }
                } else {
                    // Public routes: set authenticated immediately, fetch profile in background
                    // so it's ready by the time user navigates to settings/alerts
                    setIsAuthenticated(true);
                    userAPI.getBootstrap()
                        .then((bootstrap) => applyBootstrapPayload(bootstrap))
                        .catch(err => console.debug('Background profile fetch failed:', err.message));
                }
            } else {
                setAccessToken(null);
                setRefreshToken(null);
                clearBootstrapWatchedAddresses();
            }
        } catch (error) {
            console.error('❌ Auth check failed:', error);
            // Clear invalid tokens so user can re-authenticate
            if (error.response?.status === 401 || error.response?.status === 403) {
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                setAccessToken(null);
                setRefreshToken(null);
                clearBootstrapWatchedAddresses();
            }
        } finally {
            clearTimeout(timeoutId);
            setLoading(false);
        }
    };

    const refreshProfile = async () => {
        try {
            await hydrateBootstrap();
            return { success: true };
        } catch (error) {
            return { success: false, error };
        }
    };

    const login = async (email, password) => {
        try {
            const response = await authAPI.login({ email, password });

            // Since authAPI.login already returns response.data, we destructure directly from response
            const { user: userData, tokens } = response;

            localStorage.setItem('accessToken', tokens.access.token);
            localStorage.setItem('refreshToken', tokens.refresh.token);
            setAccessToken(tokens.access.token);
            setRefreshToken(tokens.refresh.token);

            setUser(userData);
            setIsAuthenticated(true);
            setHasLoggedOut(false); // Reset logout flag on successful login

            // Refresh profile to ensure we have latest subscription data
            try {
                await hydrateBootstrap();
            } catch (profileError) {
                // If profile refresh fails, use the user data from login response
                console.warn('Failed to refresh profile after login:', profileError.message);
            }

            // Track login event in Google Analytics
            trackLogin('email');
            if (userData?.id) setUserId(userData.id);

            toast.success('Login successful!');
            return { success: true };
        } catch (error) {
            const message = error.response?.data?.message || error.message || 'Login failed';
            toast.error(message);
            return { success: false, error: message };
        }
    };

    const register = async (userData) => {
        try {
            const response = await authAPI.register(userData);
            const { user: newUser, tokens } = response; // authAPI.register already returns response.data

            localStorage.setItem('accessToken', tokens.access.token);
            localStorage.setItem('refreshToken', tokens.refresh.token);
            setAccessToken(tokens.access.token);
            setRefreshToken(tokens.refresh.token);

            setUser(newUser);
            setIsAuthenticated(true);

            // Track signup event in Google Analytics
            trackSignUp('email');
            if (newUser?.id) setUserId(newUser.id);

            toast.success('Registration successful!');
            return { success: true };
        } catch (error) {
            const message = error.response?.data?.message || 'Registration failed';
            toast.error(message);
            return { success: false, error: message };
        }
    };

    const logout = async () => {
        try {
            const refreshToken = localStorage.getItem('refreshToken');

            // Always attempt to logout on server, even if no refresh token
            try {
                await authAPI.logout({ refreshToken: refreshToken || '' });
            } catch (apiError) {
                // Log the API error but don't block logout
                // Still proceed with local logout - user experience should not be blocked
            }
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            // Always clean up local state regardless of server response
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            setAccessToken(null);
            setRefreshToken(null);
            setUser(null);
            setIsAuthenticated(false);
            setHasLoggedOut(true); // Mark that user intentionally logged out
            clearBootstrapWatchedAddresses();

            toast.success('Logged out successfully');
        }
    };

    const updateProfile = async (data) => {
        try {
            const response = await userAPI.updateProfile(data);
            setUser(response); // userAPI.updateProfile already returns response.data
            toast.success('Profile updated successfully');
            return { success: true };
        } catch (error) {
            const message = error.response?.data?.message || 'Profile update failed';
            toast.error(message);
            return { success: false, error: message };
        }
    };

    const forgotPassword = async (email) => {
        try {
            await authAPI.forgotPassword({ email });
            toast.success('Password reset email sent');
            return { success: true };
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to send reset email';
            toast.error(message);
            return { success: false, error: message };
        }
    };

    const resetPassword = async (token, password) => {
        try {
            await authAPI.resetPassword({ token, password });
            toast.success('Password reset successful');
            return { success: true };
        } catch (error) {
            const message = error.response?.data?.message || 'Password reset failed';
            toast.error(message);
            return { success: false, error: message };
        }
    };

    // Set tokens directly (used by OAuth callbacks like Google)
    const setTokens = async ({ accessToken: newAccessToken, refreshToken: newRefreshToken }) => {
        try {
            // Store tokens in localStorage
            localStorage.setItem('accessToken', newAccessToken);
            if (newRefreshToken) {
                localStorage.setItem('refreshToken', newRefreshToken);
            }
            
            // Update state
            setAccessToken(newAccessToken);
            if (newRefreshToken) {
                setRefreshToken(newRefreshToken);
            }
            
            // Fetch user profile with the new token
            await hydrateBootstrap();
            setIsAuthenticated(true);
            setHasLoggedOut(false);
            
            return { success: true };
        } catch (error) {
            console.error('Failed to set tokens:', error);
            return { success: false, error };
        }
    };

    const value = {
        user,
        token: accessToken,
        loading,
        isAuthenticated,
        hasLoggedOut,
        setHasLoggedOut,
        login,
        register,
        logout,
        updateProfile,
        refreshProfile,
        forgotPassword,
        resetPassword,
        setTokens, // For OAuth callbacks (Google, etc.)
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}; 